import { INSTANT_CLOSE_MS } from "@/features/positions/utils/instant-close";

/**
 * Close-reason toast — parse the labeled account-close event and batch a
 * group flatten into ONE Donna-voiced toast.
 *
 * Classification is the backend's job (order metadata source). This module
 * never invents Cash Exit / Profit Protection from a missing mechanism.
 */

export const CLOSE_REASON_KIND = "account_close_reason";
export const OPEN_REASON_KIND = "account_open_reason";
export const CLOSE_REASON_WAVE_MS = 3500;

// Manual flattens trickle in as the member closes account after account from
// the broker UI — a much longer fold window keeps that ONE quiet summary toast
// (the stable sonner id already replaces in place; this widens the count).
export const MANUAL_WAVE_MS = 30_000;

export function waveWindowMsFor(mechanism: CloseMechanism): number {
  // Broker-side closes trickle account-by-account (member or firm working
  // down a list) — same long fold window as manual.
  return mechanism === "manual" || mechanism === "broker_close" || mechanism === "firm_liquidation"
    ? MANUAL_WAVE_MS
    : CLOSE_REASON_WAVE_MS;
}

export type CloseMechanism =
  | "cash_exit"
  | "profit_protection"
  | "risk_stop"
  | "emergency_flatten"
  | "manual"
  | "firm_liquidation"
  | "broker_close"
  | "auto_close"
  | "copy_mirror"
  | "news_window"
  | "strategy_exit"
  | "group_flatten"
  | "strategy_entry";

export type CloseReasonEvent = {
  mechanism: CloseMechanism;
  mechanismLabel: string;
  celebratory: boolean;
  accountName: string;
  accountShort: string;
  realizedPnl: number | null;
  groupName: string | null;
  source: string;
  sourceDetail: string;
  cashExitLine: number | null;
  symbol: string;
  kind: typeof CLOSE_REASON_KIND | typeof OPEN_REASON_KIND;
  holdMs: number | null;
  instantClose: boolean;
};

const MECHANISM_SET = new Set<CloseMechanism>([
  "cash_exit",
  "profit_protection",
  "risk_stop",
  "emergency_flatten",
  "manual",
  "firm_liquidation",
  "broker_close",
  "auto_close",
  "copy_mirror",
  "news_window",
  "strategy_exit",
  "group_flatten",
  "strategy_entry",
]);

export const CLOSE_MECHANISM_LABELS: Record<string, string> = {
  cash_exit: "Cash Exit",
  profit_protection: "Profit Protection",
  risk_stop: "Risk Stop",
  emergency_flatten: "Emergency Flatten",
  strategy_exit: "Strategy Exit",
  group_flatten: "Group Flatten",
  manual: "Manual",
  firm_liquidation: "Firm Risk Desk",
  broker_close: "Broker Close",
  auto_close: "Auto-close",
  copy_mirror: "Copier",
  news_window: "News Window",
  strategy_entry: "Strategy Entry",
};

const LABELS = CLOSE_MECHANISM_LABELS as Record<CloseMechanism, string>;

export function accountShortName(accountName: string | null | undefined): string {
  const raw = String(accountName ?? "").trim();
  if (!raw) return "account";
  const chars = raw.replace(/[^a-zA-Z0-9]/g, "");
  if (chars.length >= 5) return chars.slice(-5);
  return raw;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asMechanism(value: unknown): CloseMechanism {
  const token = asString(value).toLowerCase();
  if (MECHANISM_SET.has(token as CloseMechanism)) {
    return token as CloseMechanism;
  }
  return "auto_close";
}

export function parseCloseReasonAlert(
  alert: { metadata?: Record<string, unknown> | null; extra_data?: Record<string, unknown> | null },
): CloseReasonEvent | null {
  const meta = {
    ...asRecord(alert.extra_data),
    ...asRecord(alert.metadata),
  };
  const kind = asString(meta.kind);
  if (kind !== CLOSE_REASON_KIND && kind !== OPEN_REASON_KIND) {
    return null;
  }
  const mechanism = asMechanism(meta.mechanism);
  const accountName = asString(meta.account_name) || "account";
  const celebratory = mechanism === "cash_exit" || mechanism === "profit_protection";
  return {
    mechanism,
    mechanismLabel: asString(meta.mechanism_label) || LABELS[mechanism],
    celebratory,
    accountName,
    accountShort: accountShortName(accountName),
    realizedPnl: asNumber(meta.realized_pnl),
    groupName: asString(meta.group_name) || null,
    source: asString(meta.source),
    sourceDetail: asString(meta.source_detail),
    cashExitLine: asNumber(meta.cash_exit_line),
    symbol: asString(meta.symbol),
    kind: kind === OPEN_REASON_KIND ? OPEN_REASON_KIND : CLOSE_REASON_KIND,
    holdMs: asNumber(meta.hold_ms),
    instantClose:
      meta.instant_close === true ||
      (asNumber(meta.hold_ms) != null && (asNumber(meta.hold_ms) as number) < INSTANT_CLOSE_MS),
  };
}

export type CloseReasonRow = {
  /** Display label — full account name solo, short suffix in a wave. */
  label: string;
  pnl: number | null;
};

export type CloseReasonCopy = {
  eyebrow: string;
  title: string;
  description: string;
  /** Mechanism WHY line — always visible, even when per-account rows render. */
  why?: string;
  instantClose?: boolean;
  celebratory: boolean;
  /** Per-account rows for the card body (newest wave order). */
  rows: CloseReasonRow[];
  /** Sum of known per-account P&L; null when no account reported one. */
  totalPnl: number | null;
  mechanism: CloseMechanism;
};

function formatPnl(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "";
  const sign = value >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

function formatCashLine(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs === Math.trunc(abs)) return `at your ${sign}$${abs} line`;
  return `at your ${sign}$${abs.toFixed(2)} line`;
}

export function openMechanismWhy(event: CloseReasonEvent): string {
  if (event.mechanism === "copy_mirror") return "copier mirrored leader open";
  if (event.mechanism === "news_window") return "news-window entry";
  if (event.mechanism === "manual") return "broker-side manual";
  if (event.mechanism === "broker_close") return "opened at the broker — source unknown";
  if (event.mechanism === "strategy_entry" || event.mechanism === "strategy_exit") {
    return event.sourceDetail || "strategy entry";
  }
  return event.sourceDetail || "strategy entry";
}

export function closeMechanismWhy(events: CloseReasonEvent[], pnlText: string): string {
  const event = events[0];
  if (!event) return "";
  if (event.kind === OPEN_REASON_KIND) return openMechanismWhy(event);
  switch (event.mechanism) {
    case "cash_exit": {
      const line = formatCashLine(event.cashExitLine);
      if (pnlText && line) return `banked ${pnlText} ${line}`;
      if (pnlText) return `banked ${pnlText}`;
      if (line) return `cash-exit ${line}`;
      return "cash-exit";
    }
    case "copy_mirror":
      return "copier mirrored leader close";
    case "news_window":
      return "news-window protective close";
    case "manual":
      return "broker-side manual";
    case "broker_close":
      return "closed at the broker — source unknown";
    case "firm_liquidation":
      return "closed by firm risk desk";
    case "profit_protection":
      return joinDetail("Profit Protection", pnlText);
    case "risk_stop":
      return "Risk Stop";
    case "emergency_flatten":
      return "Emergency Flatten";
    case "strategy_exit":
      return event.sourceDetail || "Strategy Exit";
    case "group_flatten":
      return "Group Flatten";
    default:
      return event.sourceDetail || LABELS[event.mechanism] || "Auto-close";
  }
}

function joinDetail(left: string, pnl: string): string {
  if (left && pnl) return `${left} · ${pnl}`;
  return left || pnl;
}

export function formatCloseReasonToast(events: CloseReasonEvent[]): CloseReasonCopy | null {
  if (events.length === 0) return null;
  const mechanism = events[0].mechanism;
  const celebratory = mechanism === "cash_exit" || mechanism === "profit_protection";
  const names = events.map((event) => event.accountName);
  const shorts = events.map((event) => event.accountShort);
  const pnlValues = events
    .map((event) => event.realizedPnl)
    .filter((value): value is number => value != null && Number.isFinite(value));
  const pnlTotal = pnlValues.length > 0 ? pnlValues.reduce((sum, value) => sum + value, 0) : null;
  const pnlText = formatPnl(pnlTotal);
  const count = shorts.length;
  const why = closeMechanismWhy(events, pnlText);
  // Full name when the card shows one account, short suffix in a wave — same
  // rule the copy strings already follow.
  const base = {
    rows: events.map((event) => ({
      label: count > 1 ? event.accountShort : event.accountName,
      pnl: event.realizedPnl,
    })),
    totalPnl: pnlTotal,
    mechanism,
    why,
    instantClose: events.some((event) => event.instantClose),
  };

  if (mechanism === "cash_exit") {
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Cash Exit",
        title: `Cash Exit hit on ${count} accounts! 💵`,
        description: joinDetail(shorts.join(", "), why),
        celebratory: true,
      };
    }
    return {
      ...base,
      eyebrow: "Cash Exit",
      title: "Cash Exit hit! 💵",
      description: joinDetail(names[0] ?? "account", why),
      celebratory: true,
    };
  }

  if (mechanism === "profit_protection") {
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Profit Protection",
        title: `Profit locked on ${count} accounts! 💵`,
        description: joinDetail(shorts.join(", "), pnlText),
        celebratory: true,
      };
    }
    return {
      ...base,
      eyebrow: "Profit Protection",
      title: "Profit locked! 💵",
      description: joinDetail(names[0] ?? "account", pnlText),
      celebratory: true,
    };
  }

  if (mechanism === "manual") {
    // The mechanism must stay visible on the card even in rows-mode (which
    // replaces the description) — the eyebrow carries it (operator ask).
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Manual — your login",
        title: `${count} accounts closed`,
        description: joinDetail(`${why} · ${shorts.join(", ")}`, pnlText),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow: "Manual — your login",
      title: `${names[0] ?? "Account"} closed`,
      description: joinDetail(why, pnlText),
      celebratory: false,
    };
  }

  if (mechanism === "firm_liquidation") {
    // SIG-1665: the firm's action, never the member's — no "your login".
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Firm Risk Desk",
        title: `${count} accounts closed`,
        description: joinDetail(`closed by firm risk desk · ${shorts.join(", ")}`, pnlText),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow: "Firm Risk Desk",
      title: `${names[0] ?? "Account"} closed`,
      description: joinDetail("closed by firm risk desk", pnlText),
      celebratory: false,
    };
  }

  if (mechanism === "broker_close") {
    // SIG-1665: broker-side close with no initiator proof — neutral label.
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Broker Close",
        title: `${count} accounts closed`,
        description: joinDetail(`${why} · ${shorts.join(", ")}`, pnlText),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow: "Broker Close",
      title: `${names[0] ?? "Account"} closed`,
      description: joinDetail(why, pnlText),
      celebratory: false,
    };
  }

  if (mechanism === "risk_stop") {
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Risk Stop",
        title: `${count} accounts closed`,
        description: joinDetail(`Risk Stop · ${shorts.join(", ")}`, pnlText),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow: "Risk Stop",
      title: `${names[0] ?? "Account"} closed`,
      description: joinDetail("Risk Stop", pnlText),
      celebratory: false,
    };
  }

  if (mechanism === "emergency_flatten") {
    if (count > 1) {
      return {
        ...base,
        eyebrow: "Emergency Flatten",
        title: `${count} accounts closed`,
        description: joinDetail(`Emergency Flatten · ${shorts.join(", ")}`, pnlText),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow: "Emergency Flatten",
      title: `${names[0] ?? "Account"} closed`,
      description: joinDetail("Emergency Flatten", pnlText),
      celebratory: false,
    };
  }

  const isOpen = events[0]?.kind === OPEN_REASON_KIND;
  const eyebrow = LABELS[mechanism] || "Auto-close";
  if (isOpen) {
    const instrument = events[0]?.symbol || "Trade";
    if (count > 1) {
      return {
        ...base,
        eyebrow,
        title: `${instrument} opened on ${count} accounts`,
        description: joinDetail(why, shorts.join(", ")),
        celebratory: false,
      };
    }
    return {
      ...base,
      eyebrow,
      title: `${instrument} opened on ${names[0] ?? "Account"}`,
      description: why,
      celebratory: false,
    };
  }
  if (count > 1) {
    return {
      ...base,
      eyebrow,
      title: `${count} accounts closed`,
      description: joinDetail(`${why} · ${shorts.join(", ")}`, pnlText),
      celebratory: false,
    };
  }
  return {
    ...base,
    eyebrow,
    title: `${names[0] ?? "Account"} closed`,
    description: joinDetail(why, pnlText),
    celebratory,
  };
}

export function tradeEventPresentation(notification: {
  title?: string | null;
  message?: string | null;
  extra_data?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}): { mechanismLabel: string | null; why: string; kind: "open" | "close" | null } {
  const parsed = parseCloseReasonAlert(notification);
  if (!parsed) {
    return { mechanismLabel: null, why: "", kind: null };
  }
  const copy = formatCloseReasonToast([parsed]);
  return {
    mechanismLabel: parsed.mechanismLabel,
    why: copy?.why || parsed.sourceDetail,
    kind: parsed.kind === OPEN_REASON_KIND ? "open" : "close",
  };
}

// ============================================================================
// Inbox fold — one card per close (SIG-1664)
// ============================================================================

// The backend wave slot lets a NAMED mechanism upgrade an `auto_close` claim
// by dispatching a SECOND inbox record for the same close. The toast layer
// already folds by mechanism; the inbox must too, or the member sees both the
// generic "Auto-close" card and the named upgrade for ONE close. Window is
// generous vs the backend's 8s slot TTL to absorb dispatch/persist lag.
export const CLOSE_REASON_INBOX_FOLD_MS = 30_000;

type InboxNotificationLike = {
  created_at: string;
  extra_data?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
};

function closeReasonMeta(notification: InboxNotificationLike): Record<string, unknown> | null {
  const meta = {
    ...asRecord(notification.extra_data),
    ...asRecord(notification.metadata),
  };
  const kind = asString(meta.kind);
  return kind === CLOSE_REASON_KIND || kind === OPEN_REASON_KIND ? meta : null;
}

function closeReasonAccount(meta: Record<string, unknown>): string {
  const raw = meta.broker_account_id;
  return raw == null ? "" : String(raw).trim();
}

function closeReasonTime(notification: InboxNotificationLike, meta: Record<string, unknown>): number {
  const triggered = Date.parse(asString(meta.triggered_at));
  if (Number.isFinite(triggered)) return triggered;
  const created = Date.parse(notification.created_at);
  return Number.isFinite(created) ? created : 0;
}

/**
 * Hide a generic `auto_close` inbox record when a NAMED-mechanism record
 * exists for the same close (same broker account, same wave window). The
 * named card is the authoritative upgrade — never show both.
 */
export function foldCloseReasonInbox<T extends InboxNotificationLike>(
  notifications: T[],
  windowMs: number = CLOSE_REASON_INBOX_FOLD_MS,
): T[] {
  const named: { account: string; time: number }[] = [];
  for (const notification of notifications) {
    const meta = closeReasonMeta(notification);
    if (!meta) continue;
    if (asMechanism(meta.mechanism) === "auto_close") continue;
    const account = closeReasonAccount(meta);
    if (!account) continue;
    named.push({ account, time: closeReasonTime(notification, meta) });
  }
  if (named.length === 0) return notifications;
  return notifications.filter((notification) => {
    const meta = closeReasonMeta(notification);
    if (!meta) return true;
    if (asMechanism(meta.mechanism) !== "auto_close") return true;
    const account = closeReasonAccount(meta);
    if (!account) return true;
    const time = closeReasonTime(notification, meta);
    return !named.some(
      (record) => record.account === account && Math.abs(record.time - time) <= windowMs,
    );
  });
}

export type CloseReasonWave = {
  openedAt: number;
  events: CloseReasonEvent[];
};

export function mergeCloseReasonWave(
  waves: Map<CloseMechanism, CloseReasonWave>,
  event: CloseReasonEvent,
  now: number,
  windowMs: number = CLOSE_REASON_WAVE_MS,
): CloseReasonEvent[] {
  const open = waves.get(event.mechanism);
  const wave: CloseReasonWave =
    open && now - open.openedAt < windowMs ? open : { openedAt: now, events: [] };
  if (!open || now - open.openedAt >= windowMs) {
    waves.set(event.mechanism, wave);
  }
  const existing = wave.events.find((row) => row.accountName === event.accountName);
  if (existing) {
    if (existing.realizedPnl == null) existing.realizedPnl = event.realizedPnl;
    else if (event.realizedPnl != null) existing.realizedPnl += event.realizedPnl;
  } else {
    wave.events.push(event);
  }
  return wave.events;
}
